import {AddMatchDto, MatchBasicInfoDto, MatchInfoDto} from "sandy-shared/dist/dtos/match-info.dto";
import {CompleteSetDto} from "sandy-shared/dist/dtos/match-actions.dto";
import {authenticatedFetch} from "../shared/services/http.service";

export const getMatch = async (matchId: number): Promise<MatchInfoDto> => {
  const result = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}`,
    { headers: { 'Content-Type': 'application/json' } },
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch match: ${response.statusText}`);
    }
    return await response.json() as MatchInfoDto;
  });
  return result;
}

export interface GetMatchesOptions {
  season?: string;          // Omit for all seasons
  includeDeleted?: boolean; // Soft-deleted matches are excluded unless this is true
}

export const getMatches = async (options: GetMatchesOptions = {}): Promise<MatchInfoDto[]> => {
  const params = new URLSearchParams();
  if (options.season) params.set('season', options.season);
  if (options.includeDeleted) params.set('includeDeleted', 'true');
  const query = params.toString();
  const result = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches${query ? `?${query}` : ''}`,
    { headers: { 'Content-Type': 'application/json' } },
  ).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to fetch matches: ${response.statusText}`);
    }
    return await response.json() as MatchInfoDto[]
  });
  return result;
}

export async function restoreMatch(matchId: number): Promise<MatchInfoDto> {
  const result = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}/restore`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
    }).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to restore match: ${response.statusText}`);
    }
    return await response.json() as MatchInfoDto
  });
  return result;
}

export async function addMatch(match: AddMatchDto): Promise<MatchInfoDto> {
  const createdMatch = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
      body: JSON.stringify(match)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to add match: ${response.statusText}`);
      }
      return await response.json() as MatchInfoDto
    });
  return createdMatch;
}

export async function updateMatch(matchId: number, updatedMatch: Partial<AddMatchDto>): Promise<MatchInfoDto> {
  const updatedMatchResult = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json'},
      body: JSON.stringify(updatedMatch)
    }).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to update match: ${response.statusText}`);
    }
    return await response.json() as MatchInfoDto
  });
  return updatedMatchResult;
}

export async function deleteMatch(matchId: number): Promise<void> {
  await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json'},
    }).then(async response => {
    if (!response.ok) {
      throw new Error(`Failed to delete match: ${response.statusText}`);
    }
  });
}

export async function completeSet(matchId: number, dto: CompleteSetDto): Promise<MatchInfoDto> {
  const result = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}/complete-set`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
      body: JSON.stringify(dto)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to complete set: ${response.statusText}`);
      }
      return await response.json() as MatchInfoDto
    });
  return result;
}

export async function startNewSet(matchId: number): Promise<MatchInfoDto> {
  const result = await authenticatedFetch(
    `${process.env.REACT_APP_SANDY_API_BASE_URL}/api/v1/matches/${matchId}/start-new-set`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to start set: ${response.statusText}`);
      }
      return await response.json() as MatchInfoDto
    });
  return result;
}